/* ==================================================================
   IVYN CARD MEDIA — intent-gated image loading
   Phase 1 — IVYN Speed Fix – Aug 2026 (duplicate theme only)
   ------------------------------------------------------------------
   WHY THIS EXISTS
   theme.js already intended extras to stay unloaded until interaction
   (see warmOne(): "a 20-card grid still ships 20 images on first paint
   rather than 40+"). It relied on loading="lazy", which does NOT defer
   these images: the extra slides live inside .pc-track, which sits
   inside the in-viewport .pi container, so the browser treats them as
   near-viewport and fetches all of them immediately.

   FIX
   product-card.liquid now emits hover + extra slides with data-src /
   data-srcset and NO src, so they are never requested on first paint.
   This file promotes them to real src on genuine intent only:
     - pointerenter (desktop hover)
     - touchstart / pointerdown (mobile swipe intent)
     - focusin (keyboard)
     - arrow click (ivynCardNav)
   Once a card is warmed it stays warmed.

   ADDITIVE BY DESIGN: theme.js is NOT modified. Its warmOne() lazy->eager
   flip becomes a harmless no-op on these images. Swatch clicks in theme.js
   assign .src directly, which is already deliberate interaction, so those
   paths keep working unchanged.
   ================================================================== */
(function () {
  "use strict";

  function promote(img) {
    if (!img) return;
    var ds = img.getAttribute("data-src");
    if (!ds) return;                     /* already promoted or never deferred */
    var dss = img.getAttribute("data-srcset");
    if (dss) img.setAttribute("srcset", dss);
    img.setAttribute("src", ds);
    img.removeAttribute("data-src");
    img.removeAttribute("data-srcset");
  }

  function warmCard(pi) {
    if (!pi || pi.dataset.mediaWarmed) return;
    pi.dataset.mediaWarmed = "1";
    var imgs = pi.querySelectorAll("img[data-src]");
    for (var i = 0; i < imgs.length; i++) promote(imgs[i]);
  }

  function fromEvent(e) {
    var t = e.target;
    if (!t || !t.closest) return null;
    return t.closest(".pi");
  }

  /* Delegated so cards injected later by filters/pagination are covered
     without re-binding. All passive — no scroll cost. */
  var OPTS = { passive: true, capture: true };

  document.addEventListener("pointerenter", function (e) {
    if (e.pointerType === "touch") return;   /* touch handled below */
    var pi = fromEvent(e);
    if (pi) warmCard(pi);
  }, OPTS);

  document.addEventListener("touchstart", function (e) {
    var pi = fromEvent(e);
    if (pi) warmCard(pi);
  }, OPTS);

  document.addEventListener("pointerdown", function (e) {
    var pi = fromEvent(e);
    if (pi) warmCard(pi);
  }, OPTS);

  document.addEventListener("focusin", function (e) {
    var pi = fromEvent(e);
    if (pi) warmCard(pi);
  }, OPTS);

  /* Arrow buttons call window.ivynCardNav (theme.js). Wrap it so the card
     is warmed BEFORE theme.js measures slide count and moves the track. */
  function wrapNav() {
    var orig = window.ivynCardNav;
    if (typeof orig !== "function" || orig.__ivynWrapped) return;
    var wrapped = function (btn, dir) {
      try {
        var wrap = btn && btn.closest ? btn.closest(".pc-wrap") : null;
        if (wrap) warmCard(wrap.querySelector(".pi"));
      } catch (err) {}
      return orig.apply(this, arguments);
    };
    wrapped.__ivynWrapped = true;
    window.ivynCardNav = wrapped;
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", wrapNav);
  } else {
    wrapNav();
  }
  window.addEventListener("load", wrapNav);

  /* Safety net: if JS-driven intent never fires but the shopper scrolls the
     card fully into view and lingers, promote anyway so nothing is
     permanently invisible. Uses IntersectionObserver with a deliberate
     delay — this is a fallback, not the primary path. */
  if ("IntersectionObserver" in window) {
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (!en.isIntersecting) return;
        var pi = en.target;
        io.unobserve(pi);
        setTimeout(function () { warmCard(pi); }, 2500);
      });
    }, { rootMargin: "0px", threshold: 0.9 });

    var observeAll = function () {
      document.querySelectorAll(".pi").forEach(function (pi) {
        if (!pi.dataset.mediaObserved && !pi.dataset.mediaWarmed) {
          pi.dataset.mediaObserved = "1";
          io.observe(pi);
        }
      });
    };
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", observeAll);
    } else {
      observeAll();
    }
    window.addEventListener("load", observeAll);
  }

  /* No-JS / failed-JS fallback: promote everything after full load + 6s.
     Guarantees hover and extra images are never permanently unreachable. */
  window.addEventListener("load", function () {
    setTimeout(function () {
      document.querySelectorAll(".pi").forEach(warmCard);
    }, 6000);
  });
})();
